// app/components/SimilarCampsitesGrid.tsx
'use client';

/**
 * SimilarCampsitesGrid — photo-led card grid for the "Similar" section on a
 * campsite page, matching the Nearby Campsites redesign.
 *
 * Data is fetched server-side and passed in via props (so the page keeps its
 * SSR). This component owns image resilience: it resolves the best available
 * photo and, crucially, falls back on `onError` so a broken/blocked image_src
 * never renders blank (the old inline list had no fallback).
 */
import { withBase } from '../lib/brand';
import { stripHtml } from '../lib/richText';
import React, { useState } from 'react';
import Link from 'next/link';
import { SimilarCampsite } from '../camp-sites/load-entities';
import { isLocalPublicImage, toOptimizedImageSrc } from '../utils/normalizeImageSrc';

type DefaultImages = {
  beach: string;
  forest: string;
  mountain: string;
  lake: string;
  desert: string;
  default: string;
};

interface Props {
  campsites: SimilarCampsite[];
  defaultImage: string;
  defaultImages?: DefaultImages;
  sourceEntityId: number;
}

// Some entities carry a junk image value ("no", "", "RIDB feed (US)") instead
// of a real URL. Real image references (URL, S3 key, filename) contain '.' or '/'.
function cleanSrc(src?: string | null): string | null {
  if (!src) return null;
  const s = src.trim();
  if (!s || s.toLowerCase() === 'no' || s.toLowerCase() === 'none') return null;
  if (!/[./]/.test(s)) return null;
  return s;
}

// Remote photos are stored as bare imgix base URLs; ask the CDN for a sensibly
// sized, auto-formatted crop instead of shipping the full-resolution original.
// A bare S3 key ("/_assets/images/camp-sites/...") goes through imgix too:
// as a plain <img> src it resolved against the store's domain and 404'd
// (tathra-beachside on every campsite page that listed it as similar).
function sized(src: string): string {
  if (src.includes('imgix.net') && !src.includes('?')) {
    return `${src}?w=800&h=600&fit=crop&crop=entropy&auto=format%2Ccompress&q=70`;
  }
  if (!/^(https?:)?\/\//i.test(src) && !isLocalPublicImage(src)) {
    return toOptimizedImageSrc(src, { width: 800, quality: 70 });
  }
  return src;
}

function defaultByType(
  campsite: SimilarCampsite,
  index: number,
  defaultImages?: DefaultImages,
): string {
  if (!defaultImages) return '';
  const name = campsite.name?.toLowerCase() || '';
  const features = campsite.features?.map((f) => f.desc).join(' ').toLowerCase() || '';
  const combined = `${name} ${features}`;

  if (combined.includes('beach') || combined.includes('coast')) return defaultImages.beach;
  if (combined.includes('forest') || combined.includes('bush')) return defaultImages.forest;
  if (combined.includes('mountain') || combined.includes('alpine')) return defaultImages.mountain;
  if (combined.includes('lake') || combined.includes('river')) return defaultImages.lake;
  if (combined.includes('desert') || combined.includes('outback')) return defaultImages.desert;

  const types: (keyof DefaultImages)[] = ['beach', 'forest', 'mountain', 'lake', 'desert'];
  return defaultImages[types[index % types.length]] || defaultImages.default;
}

const normalizeEntityName = (name: string): string =>
  name
    .replace(/['‘’`]/g, '')
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/gi, '-')
    .toLowerCase()
    .trim()
    .replace(/^-+|-+$/g, '');

const badgeClass = (label?: string): string => {
  if (label === 'Very Similar') return 'bg-emerald-50 text-emerald-700 ring-emerald-100';
  if (label === 'Similar') return 'bg-[#e3f2fd] text-[#1558b0] ring-[#e3f2fd]';
  return 'bg-slate-100 text-slate-600 ring-slate-200';
};

export default function SimilarCampsitesGrid({
  campsites,
  defaultImage,
  defaultImages,
  sourceEntityId,
}: Props) {
  // Some entities carry a stored image URL whose CDN asset has since 404'd.
  // We can't know that until the browser tries to load it, so on error we mark
  // the id failed and re-render with the local default (next/image controls the
  // srcset, so swapping via state is more reliable than mutating the DOM node).
  const [failedIds, setFailedIds] = useState<Record<number, boolean>>({});

  if (!campsites || campsites.length === 0) return null;

  return (
    <div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {campsites.slice(0, 6).map((campsite, index) => {
          const galleryFirst = campsite.gallery_images?.[0]?.image_src;
          const typeDefault = defaultByType(campsite, index, defaultImages) || defaultImage;
          const imageSrc = failedIds[campsite.id]
            ? typeDefault
            : cleanSrc(campsite.image_src) || cleanSrc(galleryFirst) || typeDefault;

          const slug = normalizeEntityName(campsite.name || '');
          const distanceKm =
            typeof campsite.distance_km === 'number'
              ? campsite.distance_km
              : parseFloat(String(campsite.distance_km || '0'));
          const town = campsite.addresses?.[0]?.town || '';
          const state = campsite.addresses?.[0]?.state || 'Australia';
          const loc = town ? `${town}, ${state}` : state;
          // stripHtml: RIDB (US) descriptions arrive as HTML fragments.
          const blurb = stripHtml(campsite.short_desc || campsite.desc || '');

          return (
            <Link
              key={campsite.id}
              href={withBase(`/camp-sites/${slug}`)}
              className="group block overflow-hidden rounded-xl border border-slate-200 bg-white transition hover:shadow-lg"
            >
              <div className="relative aspect-[4/3] w-full overflow-hidden bg-slate-100">
                {/* Plain <img> (not next/image): remote photos here are stored as
                    CDN URLs that can 404, and next/image swallows the error event
                    on its srcset — a plain img fires onError reliably so we can
                    fall back to a local default instead of rendering blank. */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={sized(imageSrc)}
                  alt={`${campsite.name}${loc ? ` — ${loc}` : ''}`}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition duration-300 group-hover:scale-[1.04]"
                  onError={() =>
                    setFailedIds((prev) =>
                      prev[campsite.id] ? prev : { ...prev, [campsite.id]: true },
                    )
                  }
                />
                {campsite.similarity_label && (
                  <span
                    className={`absolute right-3 top-3 rounded-full px-2.5 py-1 text-xs font-semibold shadow-sm ring-1 ${badgeClass(
                      campsite.similarity_label,
                    )}`}
                  >
                    {campsite.similarity_label}
                  </span>
                )}
                {distanceKm > 0 && (
                  <span className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-xs font-semibold text-slate-700 shadow-sm backdrop-blur">
                    {distanceKm < 1 ? `${Math.round(distanceKm * 1000)}m` : `${distanceKm.toFixed(1)} km`} away
                  </span>
                )}
              </div>
              <div className="p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-[#1558b0]">{loc}</p>
                <h3 className="mt-1 text-base font-semibold leading-snug text-slate-900 group-hover:text-[#1558b0]">
                  {campsite.name}
                </h3>
                {blurb && (
                  <p className="mt-1.5 line-clamp-2 text-sm leading-6 text-slate-600">{blurb}</p>
                )}
              </div>
            </Link>
          );
        })}
      </div>

      <div className="mt-6 flex justify-center">
        <Link
          href={withBase(`/results?search_type=similar&entity_id=${sourceEntityId}`)}
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
        >
          View all similar campsites
          <span aria-hidden>&rarr;</span>
        </Link>
      </div>
    </div>
  );
}
